import type { Epic } from '@helpcenter/epics-rxjs';
import { Observable } from '@helpcenter/epics-rxjs';
import { isJsmPortalRovoIntegrationEnabled } from '@helpcenter/feature-flags';

import fetchModel from 'epics/model/fetcher';
import type { RequestCreateResponse } from 'rest/request-create';
import { searchUser } from 'rest/user';
import type {
    FetchRequestCreateAction,
    FetchRequestCreateSuccess,
    FetchRequestCreateFailure,
} from 'state/actions/request-create';
import {
    FETCH_REQUEST_CREATE_MODEL,
    fetchRequestCreateSuccess,
    fetchRequestCreateFailure,
} from 'state/actions/request-create';
import type { FieldValues, UserPickerState } from 'state/persisted/request-create';
import { toFieldStates } from 'utils/request-create';
import { parseFieldUrlParams } from 'utils/request-create/url-param-parser';

import type { User } from 'view/common/fields/user-picker/user-picker-dumb/user-picker-dumb';
import type { ErrorResponse } from '../types';

interface HydratedRequestCreateResponse {
    requestCreateResponse: RequestCreateResponse;
    hydratedUrlParamOverrides: FieldValues;
}

type FieldValueLoader<T> = (
    fieldState: UserPickerState,
    fieldValueOverrides: FieldValues,
    portalId: number
) => Promise<{
    fieldId: keyof FieldValues;
    value: T;
}>;

export const requestCreateEpic: Epic<
    FetchRequestCreateAction,
    FetchRequestCreateSuccess | FetchRequestCreateFailure
> = (action$) => {
    return action$.ofType(FETCH_REQUEST_CREATE_MODEL).mergeMap((action) => {
        const portalId = action.payload && action.payload.params && action.payload.params.portalId;

        if (isJsmPortalRovoIntegrationEnabled()) {
            return fetchModel(action.payload)
                .filter((response) => !!response.reqCreate)
                .mergeMap((response) => {
                    return hydrateUrlParamOverrides(response.reqCreate!, portalId).then(
                        ({ requestCreateResponse, hydratedUrlParamOverrides }) =>
                            fetchRequestCreateSuccess({
                                ...requestCreateResponse,
                                hydratedUrlParamOverrides,
                                portalId,
                            })
                    );
                })
                .catch((error: ErrorResponse) => {
                    const params = action.payload.params;
                    return Observable.of(fetchRequestCreateFailure(portalId, params.id, error));
                });
        }

        return fetchModel(action.payload)
            .filter((response) => !!response.reqCreate)
            .map((response) => {
                return fetchRequestCreateSuccess({ ...response.reqCreate!, portalId, hydratedUrlParamOverrides: {} });
            })
            .catch((error: ErrorResponse) => {
                const params = action.payload.params;
                return Observable.of(fetchRequestCreateFailure(portalId, params.id, error));
            });
    });
};

/**
 * Builds a set of URL param overrides from the query parameters, and attempts to hydrate any overrides
 * for dynamic fields.
 *
 * This should never break the request create flow. If a dynamic field cannot be hydrated successfully (failed
 * API call), this function will proceed as though a URL override was not provided for that field.
 */
const hydrateUrlParamOverrides = async (
    requestCreateResponse: RequestCreateResponse,
    portalId: number
): Promise<HydratedRequestCreateResponse> => {
    const fieldStates = toFieldStates(requestCreateResponse.fields);
    const fieldValueOverrides = parseFieldUrlParams(fieldStates);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const asyncFieldValuePromises: ReturnType<FieldValueLoader<any>>[] = fieldStates
        .map((fieldState) => {
            switch (fieldState.type) {
                case 'userpicker':
                    return getUserPickerLoader(fieldState, fieldValueOverrides, portalId);
                default:
                    return null;
            }
        })
        .filter((loader) => loader !== null);

    const asyncFieldValueOverrides = await Promise.all(asyncFieldValuePromises).then((results) =>
        results.reduce((accumulator, result) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            accumulator[result.fieldId] = result.value;
            return accumulator;
        }, {} as FieldValues)
    );

    const hydratedUrlParamOverrides: FieldValues = Object.fromEntries(
        Object.entries({ ...fieldValueOverrides, ...asyncFieldValueOverrides }).filter(
            ([, fieldValue]) => fieldValue !== null
        )
    );

    return { requestCreateResponse, hydratedUrlParamOverrides };
};

const getUserPickerLoader: FieldValueLoader<User | null> = (
    fieldState: UserPickerState,
    fieldValueOverrides: FieldValues,
    portalId: number
) => {
    const fieldId = fieldState.id;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const userPickerQuery = fieldValueOverrides[fieldState.id];

    if (typeof userPickerQuery !== 'string') {
        return Promise.resolve({
            fieldId,
            value: null,
        });
    }

    return searchUser({
        portalId,
        query: userPickerQuery,
        fieldName: fieldId,
        fieldConfigId: fieldState.fieldConfigId,
    })
        .toPromise()
        .then((userSearchResponse) => {
            if (userSearchResponse.length === 0) {
                return {
                    fieldId,
                    value: null,
                };
            }

            const firstUserResult = userSearchResponse[0];
            return {
                fieldId: fieldState.id,
                value: {
                    accountId: firstUserResult.accountId,
                    email: firstUserResult.emailAddress,
                    iconUrl: firstUserResult.avatar,
                    label: firstUserResult.displayName,
                    value: firstUserResult.accountId,
                },
            };
        })
        .catch(() => ({
            fieldId,
            value: null,
        }));
};
